import { useState, type ReactNode } from 'react'
import { History } from 'lucide-react'
import { Wordmark } from './wordmark'
import { LiveStats } from './live-stats'
import { UserMenu } from './user-menu'
import { ThemeToggle } from './theme-toggle'
import { LocaleToggle } from './locale-toggle'
import { Button } from '#/components/ui/button'
import { PlanDrawer } from '#/components/plan/plan-drawer'
import { authClient } from '#/lib/auth-client'
import { cn } from '#/lib/utils'
import { m } from '#/paraglide/messages'

/**
 * App header, h-14. `center` (e.g. a stepper) is absolutely centred and
 * hidden on mobile, as in spec/05 §3. `left` replaces the live stats slot.
 * Signed-in users always get a "Saved plans" button that opens the plan drawer,
 * so a plan is reachable from any page (even mid-generation).
 * `compact` hides the locale/theme toggles on phones — used by dense workspace headers.
 */
export function AppHeader({
  center,
  left,
  right,
  className,
  showStats = true,
  compact = false,
}: {
  center?: ReactNode
  left?: ReactNode
  right?: ReactNode
  className?: string
  showStats?: boolean
  compact?: boolean
}) {
  const { data: session } = authClient.useSession()
  const [drawer, setDrawer] = useState(false)
  const signedIn = Boolean(session?.user)
  return (
    <header className={cn('relative flex h-14 shrink-0 items-center justify-between border-b bg-background px-3 sm:px-4', className)}>
      <div className="flex min-w-0 items-center gap-3 sm:gap-4">
        <Wordmark />
        {left ?? (showStats ? <LiveStats /> : null)}
      </div>
      {center ? <div className="absolute left-1/2 hidden -translate-x-1/2 sm:block">{center}</div> : null}
      <div className="flex shrink-0 items-center gap-1">
        {right}
        {signedIn ? (
          <Button size="icon" variant="ghost" className="size-8" onClick={() => setDrawer(true)} aria-label={m.ws_saved_plans()} title={m.ws_saved_plans()}>
            <History className="size-4" />
          </Button>
        ) : null}
        <div className={cn('flex items-center gap-1', compact && 'hidden sm:flex')}>
          <LocaleToggle />
          <ThemeToggle />
        </div>
        <div className="ml-1">
          <UserMenu />
        </div>
      </div>
      {signedIn ? <PlanDrawer open={drawer} onOpenChange={setDrawer} /> : null}
    </header>
  )
}
